// Single source of truth for SEO metadata across the site.
// Used by the <SEO /> component to emit per-page <title>, meta, OG, and JSON-LD.
//
// IMPORTANT: PUBLIC_SITE_URL must match the canonical production domain. Change once.
export const PUBLIC_SITE_URL = "https://www.krinapanchalphysio.com";

// Default brand-wide og:image. 1200x630 designed composition — stacked
// logo on the left, teal headline + tagline on the right, subtle radial
// accents. Rendered from /public/logo-exports/og-render.html via headless
// screenshot; regenerate that file + rerun the render if you want to
// change wording, colours, or layout. Falls back to Krina's portrait if
// a service page doesn't override it.
export const DEFAULT_OG_IMAGE = `${PUBLIC_SITE_URL}/og-image.jpg`;

// Brand logo asset used for schema.org Organization.logo and
// MedicalClinic.logo (Google Knowledge Panel, rich results). Uses the
// square-ish stacked variant (868x937) because Google's logo guidance
// prefers a near-square, PNG/JPG-friendly image over the wide horizontal
// variant.
export const BRAND_LOGO_IMAGE = `${PUBLIC_SITE_URL}/logo-stacked.jpg`;
export const KRINA_PORTRAIT_IMAGE = `${PUBLIC_SITE_URL}/images/krina-portrait.jpg`;

export const seoDefaults = {
  siteName: "The TMD Physio",
  twitterHandle: "", // none yet
  locale: "en_GB",
};

// Homepage SEO — title kept < 60 chars so Google doesn't truncate in SERPs.
export const homePageSEO = {
  title: "The TMD Physio – TMD, Headache & Tinnitus Relief | London",
  description: "Specialist physiotherapy for TMD, jaw pain, chronic headaches, migraines and tinnitus. Evidence-based treatment from Krina Panchal, Waterloo, London.",
  path: "/",
  image: KRINA_PORTRAIT_IMAGE,
};

// Per-service SEO (keyed by slug). The <ServicePage /> reads its slug and looks up this map.
export const servicePagesSEO = {
  "tmd-treatment": {
    title: "TMD Treatment in London | Jaw Pain Physio – The TMD Physio",
    description: "Expert physiotherapy for TMD, jaw clicking, locking and chronic jaw pain in central London. Personalised care from a TMD specialist with 15+ years' experience.",
    keywords: "TMD treatment London, jaw pain physio, TMJ specialist, temporomandibular disorder, jaw clicking treatment",
    conditionName: "Temporomandibular Joint Disorder (TMD)",
    bodyLocation: "Temporomandibular joint",
  },
  "headache-migraine-relief": {
    title: "Headache & Migraine Physiotherapy in London | The TMD Physio",
    description: "Drug-free relief from chronic tension headaches, cervicogenic headaches and migraines. Dean Watson Technique physiotherapy at Waterloo, London.",
    keywords: "headache physio London, migraine treatment, cervicogenic headache, Dean Watson technique, chronic headache relief",
    conditionName: "Chronic Headache & Migraine",
    bodyLocation: "Head, cervical spine",
  },
  "tinnitus-management": {
    title: "Tinnitus Physiotherapy in London | Somatic Tinnitus Relief",
    description: "Evidence-based physiotherapy for somatic tinnitus. Address the jaw and neck dysfunction driving your tinnitus symptoms — Waterloo, London.",
    keywords: "tinnitus physio London, somatic tinnitus treatment, ringing ears physiotherapy, TMJ tinnitus",
    conditionName: "Somatic Tinnitus",
    bodyLocation: "Inner ear, jaw, cervical spine",
  },
  "facial-pain-treatment": {
    title: "Facial Pain Treatment London | Trigeminal & Referred Pain Physio",
    description: "Specialist physiotherapy for facial pain, trigeminal neuralgia symptoms and referred facial tension. Holistic, drug-free treatment in central London.",
    keywords: "facial pain treatment London, trigeminal neuralgia physio, facial tension relief",
    conditionName: "Facial Pain (incl. Trigeminal & Referred)",
    bodyLocation: "Face, trigeminal nerve",
  },
  "massage": {
    title: "Therapeutic Massage London | Jaw, Neck & Shoulder – TMD Physio",
    description: "Targeted therapeutic massage for jaw, face, neck and shoulder tension. Intra-oral techniques and myofascial release at Waterloo, London.",
    keywords: "therapeutic massage London, intra-oral massage, myofascial release, jaw massage",
    conditionName: "Muscular Tension (Jaw, Face, Neck, Shoulders)",
    bodyLocation: "Jaw, face, neck, shoulders",
  },
};

// Referral page SEO
export const referralPageSEO = {
  title: "Refer a Patient | The TMD Physio – Waterloo, London",
  description: "Dentists, GPs and clinicians: refer a patient to The TMD Physio. Secure referral form with file upload for medical history, imaging and notes.",
  path: "/refer",
  image: DEFAULT_OG_IMAGE,
};

// The Signature Jaw Massage landing page SEO
export const wellnessMassageSEO = {
  title: "The Signature Jaw Massage London | Waterloo – TMD Physio",
  description: "The Signature Jaw Massage in Waterloo: a deeply restorative jaw, face and scalp massage for pure relaxation and the best sleep of the month.",
  path: "/wellness-massage",
  image: DEFAULT_OG_IMAGE,
  keywords: "Signature Jaw Massage, jaw massage London, face and scalp massage, Waterloo wellness, relaxing massage London, TMJ massage",
};

// Media / press / podcasts landing page SEO
export const mediaPageSEO = {
  title: "Press & Media | Krina Panchal – The TMD Physio, London",
  description: "Selected press features, podcast appearances and broadcast moments by Krina Panchal — TMD, headache, tinnitus and facial-pain specialist, London.",
  path: "/media",
  image: DEFAULT_OG_IMAGE,
  keywords: "Krina Panchal press, TMD expert media, jaw pain expert UK, headache specialist interview",
};

// Courses / CPD training landing page SEO
export const coursesPageSEO = {
  title: "TMD CPD Courses & Training | Krina Panchal – The TMD Physio",
  description: "Hands-on CPD courses for physios, osteopaths and dentists treating TMD, headaches and facial pain. Live cohorts, self-paced online, and in-house training.",
  path: "/courses",
  image: DEFAULT_OG_IMAGE,
  keywords: "TMD CPD course UK, jaw pain training, TMJ physiotherapy course, dental CPD London",
};

// Partner network landing page SEO
export const partnersPageSEO = {
  title: "Trusted Partner Network | Clinics & Dentists – The TMD Physio",
  description: "The clinical and wellbeing practitioners we collaborate with for the best possible TMD, headache, tinnitus and facial-pain care in London.",
  path: "/partners",
  image: DEFAULT_OG_IMAGE,
  keywords: "TMD partner clinics London, dentist TMD referral, neurology referral London, integrated jaw care",
};

// Knowledge Base landing page SEO
export const knowledgeBasePageSEO = {
  title: "TMD Knowledge Base | Evidence-based articles by Krina Panchal",
  description: "Evidence-based articles on TMD, headaches, migraines, tinnitus, pain science, hormones and lifestyle — by Krina Panchal, specialist TMD physiotherapist.",
  path: "/tmd-knowledge-base",
  image: KRINA_PORTRAIT_IMAGE,
  keywords: "TMD articles, jaw pain knowledge base, headache education, tinnitus blog, Krina Panchal articles, evidence-based physiotherapy",
};
